/**
 * Focus items — the daily focus feed: subscriptions, completion/dismissal
 * interactions, responses, daily session-complete state, and dev-only debug
 * helpers.
 *
 * Internal module — consumers import from the `userContentService` facade.
 */
import {
    collection,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    query,
    where,
    serverTimestamp,
    updateDoc,
    setDoc,
    increment,
    arrayUnion,
    deleteField,
    writeBatch,
    Unsubscribe,
    orderBy
} from 'firebase/firestore';
import { db } from '../firebase';
import { recordActivity } from '../activityDayService';
import { getLocalDateString } from '../../utils/dateUtils';
import {
    FocusCard,
    UserFocusItem,
    FocusItemResponse,
    FocusInteractionOptions,
    UserProfileEntry,
} from '../../types';
import {
    USER_CONTENT_COLLECTION,
    FOCUS_SUBCOLLECTION,
    FOCUS_RESPONSES_SUBCOLLECTION,
    writeUserProfileEntries,
    toIsoString,
} from './shared';
import { refreshUserContent } from './sequenceService';

const GOAL_FIELD_QUESTIONS: Record<string, string> = {
    goal_created: 'Did you create a goal?',
    goal_category: 'What spending category is your goal for?',
    goal_target_amount: 'What is your goal target amount?',
    goal_target_days: 'How many days is your goal?',
};

const mapFocusDoc = (docSnap: any): UserFocusItem => {
    const data = docSnap.data();
    return {
        // Spread all Firestore fields first (captures video/exercise metadata
        // like videoMedia, videoMediaId, videoCollection, exerciseTags, etc.)
        ...data,
        // Then override fields that need transformation or defaults
        id: docSnap.id,
        libraryId: data.libraryId || '',
        topic: data.topic || 'Mindful moment',
        title: data.title || 'Mindful moment',
        category: data.category || 'mindset',
        actionData: data.actionData || data.data,
        status: data.status || 'active',
        interactions: data.interactions || {},
        responses: data.responses || {},
        createdAt: toIsoString(data.createdAt) || new Date().toISOString(),
        updatedAt: toIsoString(data.updatedAt) || new Date().toISOString(),
        completedAt: toIsoString(data.completedAt),
        dismissedAt: toIsoString(data.dismissedAt),
    };
};

export const mapFocusItemsToCards = (items: UserFocusItem[]): FocusCard[] => {
    const today = getLocalDateString();
    return items.map((item) => {
        const completedDate = item.completedAt ? getLocalDateString(new Date(item.completedAt)) : undefined;
        return {
            id: item.id,
            title: item.title,
            description: item.subheader || item.dialogue || '',
            streak: item.interactions?.completions || 0,
            completedToday: completedDate === today,
            category: item.category || 'mindset',
            type: item.cardType || 'check-in',
            data: {
                questions: item.questions,
                completionMessage: item.completionMessage,
                topic: item.topic,
                dialogue: item.dialogue,
                prompt: item.prompt,
                isGoalOutcome: item.isGoalOutcome,
                goalOutcome: item.goalOutcome,
                ...(item.actionData || {})
            }
        };
    });
};

export const subscribeToUserFocus = (
    uid: string,
    callback: (items: UserFocusItem[], hasAnyDocs: boolean, hasStaleContent: boolean) => void
): Unsubscribe => {
    const focusRef = collection(db, USER_CONTENT_COLLECTION, uid, FOCUS_SUBCOLLECTION);
    const focusQuery = query(focusRef);
    return onSnapshot(focusQuery, (snapshot) => {
        const items = snapshot.docs.map(mapFocusDoc);
        // Every content type — insights included — follows the user's LOCAL
        // calendar day (Justin ruling 2026-07-20; insights previously used UTC).
        const today = getLocalDateString();

        const isoToCalendarDate = (iso: string | undefined): string | undefined => {
            if (!iso) return undefined;
            return getLocalDateString(new Date(iso));
        };

        // Show active items + items completed/dismissed today (so they can
        // display their completion state on the home page rather than vanishing).
        const visibleItems = items
            .filter(item => {
                if (item.status === 'active') return true;
                // Include items completed/dismissed today
                if (item.status === 'completed') {
                    // completedAt may be null during pending serverTimestamp() write.
                    // Fall back to activeDate so stale cached items (e.g. a legacy
                    // "9999-01-01" sentinel or yesterday's date) don't leak into
                    // today's feed.
                    if (!item.completedAt) return item.activeDate === today;
                    const completedDate = isoToCalendarDate(item.completedAt);
                    return completedDate === today;
                }
                if (item.status === 'dismissed') {
                    if (!item.dismissedAt) return item.activeDate === today;
                    const dismissedDate = isoToCalendarDate(item.dismissedAt);
                    return dismissedDate === today;
                }
                return false;
            })
            // Deduplicate by libraryId (keep most recent)
            .reduce<UserFocusItem[]>((acc, item) => {
                if (item.libraryId && acc.some(i => i.libraryId === item.libraryId)) {
                    return acc;
                }
                acc.push(item);
                return acc;
            }, [])
            .sort((a, b) => {
                // Sort by: sequence items first (by step order), then non-sequence by priority
                const aIsSeq = a.sequenceId ? 1 : 0;
                const bIsSeq = b.sequenceId ? 1 : 0;
                if (aIsSeq !== bIsSeq) return bIsSeq - aIsSeq; // Sequence items first

                // Within sequence items: sort by step order
                if (a.sequenceId && b.sequenceId) {
                    if (a.sequenceId === b.sequenceId) {
                        return (a.sequenceStep ?? 0) - (b.sequenceStep ?? 0);
                    }
                    // Different sequences: sort by track priority (getting-started first)
                    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
                }

                // Non-sequence items: sort by priority then recency
                const scoreA = a.rankScore ?? a.basePriority ?? 0;
                const scoreB = b.rankScore ?? b.basePriority ?? 0;
                if (scoreA !== scoreB) return scoreB - scoreA;
                return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            });

        // Stale content detection:
        // - All active items are from a previous day (activeDate < today)
        // - All active items are legacy (no deliverySource = old system, needs migration)
        // - Only layups are active (no real content to show)
        // NOTE: "all completed/dismissed" is NOT stale — that's a normal end-of-session state.
        // Content is only stale when there was never any modern content delivered today.
        const activeItems = visibleItems.filter(item => item.status === 'active');
        const nonLayupActive = activeItems.filter(item => !item.isLayupItem);
        const hasActiveItems = activeItems.length > 0;
        const allLegacy = hasActiveItems && activeItems.every(item => !item.deliverySource);
        const onlyLayups = hasActiveItems && nonLayupActive.length === 0;
        // Any active items from a previous day — need refresh to advance dates.
        // Video/exercise cards now carry a real activeDate (like focus items) and so
        // participate in this stale check; insights follow the same local calendar
        // day as everything else. Any legacy "9999-01-01" sentinel doc still sorts
        // as after today under this string compare and is migrated server-side on
        // the next delivery (one-off healer: scripts/cleanup-sentinel-active-date.py).
        const hasStaleActiveItems = activeItems.some(item => {
            if (!item.activeDate) return false;
            return item.activeDate < today;
        });
        // Check if any items (active, completed, or dismissed) were delivered today
        const hasModernContentToday = visibleItems.some(item =>
            item.deliverySource && !item.isLayupItem
        );
        // "All completed/dismissed today" is session-complete, not stale.
        // This guards against false-positive stale detection after page refresh
        // when all items have been completed within the same day.
        const allTerminalToday = visibleItems.length > 0 &&
            visibleItems.filter(i => !i.isLayupItem).length > 0 &&
            visibleItems.filter(i => !i.isLayupItem).every(i => i.status === 'completed' || i.status === 'dismissed');
        const hasStaleContent = !allTerminalToday &&
            (hasStaleActiveItems || (!hasModernContentToday && items.length > 0 && !hasActiveItems) || allLegacy || onlyLayups);

        callback(visibleItems, snapshot.size > 0, hasStaleContent);
    }, (error) => {
        console.error('Error subscribing to user focus items:', error);
        callback([], false, false);
    });
};

/** Time-on-step fields folded into a complete/skip/dismiss write (Content
 *  Engine v2, phase 0). Empty when the caller did not measure — callers
 *  outside the daily session never do — so the write shape is unchanged for
 *  them. Non-finite or negative values are ignored rather than written. */
const durationFields = (options?: FocusInteractionOptions): Record<string, unknown> => {
    const ms = options?.durationMs;
    if (typeof ms !== 'number' || !Number.isFinite(ms) || ms < 0) return {};
    const rounded = Math.round(ms);
    return {
        'interactions.lastDurationMs': rounded,
        'interactions.totalDurationMs': increment(rounded),
    };
};

export const markFocusComplete = async (
    uid: string,
    focusId: string,
    responses?: Record<string, string | number | string[]>,
    options?: FocusInteractionOptions
): Promise<void> => {
    const focusRef = doc(db, USER_CONTENT_COLLECTION, uid, FOCUS_SUBCOLLECTION, focusId);
    const focusSnap = await getDoc(focusRef);
    const calendarToday = getLocalDateString();

    const updateData: Record<string, any> = {
        status: 'completed',
        // Normalize activeDate to today (also heals any legacy "9999-01-01" sentinel doc)
        activeDate: calendarToday,
        completedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        'interactions.completions': increment(1),
        'interactions.lastCompletedAt': serverTimestamp(),
        ...durationFields(options),
    };

    // Save responses to subcollection if provided.
    // Use the date as doc ID so re-submissions within a day overwrite (not accumulate).
    if (responses && Object.keys(responses).length > 0) {
        const today = calendarToday;
        const responseDocRef = doc(db, USER_CONTENT_COLLECTION, uid, FOCUS_SUBCOLLECTION, focusId, FOCUS_RESPONSES_SUBCOLLECTION, today);
        // Check if doc exists to preserve createdAt on re-edits
        const existing = await getDoc(responseDocRef);
        const responseData: Record<string, any> = {
            responses: responses,
            date: today,
            completedAt: serverTimestamp(),
        };
        if (!existing.exists()) {
            responseData.createdAt = serverTimestamp();
        }
        await setDoc(responseDocRef, responseData, { merge: true });

        // Write first-name responses to userVars (first-name question type syncs to user profile)
        if (responses.firstName && typeof responses.firstName === 'string') {
            try {
                const uservarsRef = doc(db, 'uservars', uid);
                await setDoc(uservarsRef, { firstName: responses.firstName.trim() }, { merge: true });
            } catch (err) {
                console.error('Error writing firstName to uservars:', err);
            }
        }

        // Write normalized userProfile entries
        try {
            if (focusSnap.exists()) {
                const focusData = focusSnap.data();
                const cardType = focusData.cardType as string | undefined;
                const title = focusData.title || '';
                const libraryId = focusData.libraryId || '';
                const profileEntries: Omit<UserProfileEntry, 'id' | 'createdAt'>[] = [];
                const baseEntry = {
                    sourceType: 'focus' as const,
                    sourceId: focusId,
                    sourceLibraryId: libraryId,
                    sourceTitle: title,
                    cardType,
                    date: today,
                };

                if (cardType === 'check-in') {
                    const questions: any[] = focusData.questions || [];
                    const fieldLabelMap = new Map<string, string>();
                    questions.forEach((q: any) => {
                        if (q.fieldName && q.label) {
                            fieldLabelMap.set(q.fieldName, q.label);
                        }
                    });
                    for (const [fieldName, answer] of Object.entries(responses)) {
                        const question = fieldLabelMap.get(fieldName) || fieldName;
                        profileEntries.push({ ...baseEntry, question, answer, fieldName });
                    }
                } else if (cardType === 'journal') {
                    const journalEntry = responses.journal_entry;
                    if (journalEntry) {
                        const promptText = focusData.prompt || focusData.dialogue || '';
                        const question = promptText ? `${title}: ${promptText}` : title;
                        profileEntries.push({ ...baseEntry, question, answer: journalEntry, fieldName: 'journal_entry' });
                    }
                } else if (cardType === 'goal') {
                    for (const [fieldName, answer] of Object.entries(responses)) {
                        const question = GOAL_FIELD_QUESTIONS[fieldName];
                        if (question && answer !== '' && answer !== undefined && answer !== null) {
                            profileEntries.push({ ...baseEntry, question, answer, fieldName });
                        }
                    }
                }
                // All other card types: no profile entries

                if (profileEntries.length > 0) {
                    await writeUserProfileEntries(uid, profileEntries);
                }
            }
        } catch (err) {
            console.error('Error writing userProfile entries for focus:', err);
        }
    }

    await updateDoc(focusRef, updateData);

    // Fire-and-forget engagement tracking. Journal-type focus cards write a
    // journal entry (into focus responses), so they count as a 'journal'
    // occurrence; every other focus completion counts as 'focus'.
    const completedCardType = focusSnap.exists()
        ? (focusSnap.data()?.cardType as string | undefined)
        : undefined;
    recordActivity(uid, completedCardType === 'journal' ? 'journal' : 'focus').catch(() => {});
};

/** Records a real exposure: the item's step was actually shown in a daily
 *  session. daysShown counts these (and ONLY these — the server no longer
 *  bumps it on day rollover, Justin ruling 2026-07-20); at maxViews real
 *  exposures the server retires the item. Callers guard once-per-local-day
 *  via the live item's lastShownDate, so this is a blind increment. */
export const markFocusShown = async (uid: string, focusId: string): Promise<void> => {
    const focusRef = doc(db, USER_CONTENT_COLLECTION, uid, FOCUS_SUBCOLLECTION, focusId);
    await updateDoc(focusRef, {
        daysShown: increment(1),
        lastShownDate: getLocalDateString(),
        updatedAt: serverTimestamp()
    });
};

export const markFocusClicked = async (uid: string, focusId: string): Promise<void> => {
    const focusRef = doc(db, USER_CONTENT_COLLECTION, uid, FOCUS_SUBCOLLECTION, focusId);
    await updateDoc(focusRef, {
        'interactions.clicks': increment(1),
        'interactions.lastClickedAt': serverTimestamp(),
        updatedAt: serverTimestamp()
    });
};

export const markFocusDismissed = async (
    uid: string,
    focusId: string,
    options?: FocusInteractionOptions
): Promise<void> => {
    const focusRef = doc(db, USER_CONTENT_COLLECTION, uid, FOCUS_SUBCOLLECTION, focusId);
    const calendarToday = getLocalDateString();

    await updateDoc(focusRef, {
        status: 'dismissed',
        // Normalize activeDate to today (also heals any legacy "9999-01-01" sentinel doc)
        activeDate: calendarToday,
        dismissedAt: serverTimestamp(),
        'interactions.dismissals': increment(1),
        'interactions.lastDismissedAt': serverTimestamp(),
        ...durationFields(options),
        updatedAt: serverTimestamp()
    });
};

/** Records a daily-practice skip ("not now") on the focus doc itself — the
 *  day-scoped `dailySession.skippedIds` on the activityDays doc clears at
 *  midnight, so this is the durable signal the content ranker learns from.
 *  Deliberately leaves `status` alone: a skipped item still rolls forward
 *  exactly as before. Only content steps backed by a focus doc (focus /
 *  video / exercise) have one; the caller must not call this for mood,
 *  reflect or ending steps. Throws like any other write — callers treat it
 *  as best-effort and log. */
export const markFocusSkipped = async (
    uid: string,
    focusId: string,
    options?: FocusInteractionOptions
): Promise<void> => {
    const focusRef = doc(db, USER_CONTENT_COLLECTION, uid, FOCUS_SUBCOLLECTION, focusId);
    await updateDoc(focusRef, {
        'interactions.skips': increment(1),
        'interactions.lastSkippedAt': serverTimestamp(),
        lastSkippedDate: getLocalDateString(),
        ...durationFields(options),
        updatedAt: serverTimestamp()
    });
};

export const markFocusIncomplete = async (
    uid: string,
    focusId: string
): Promise<void> => {
    const focusRef = doc(db, USER_CONTENT_COLLECTION, uid, FOCUS_SUBCOLLECTION, focusId);
    await updateDoc(focusRef, {
        status: 'active',
        completedAt: deleteField(),
        updatedAt: serverTimestamp()
    });
};

/** Increments insightViews at most once per local calendar day. Server retires at 15 total views. */
export const markInsightFeedViewed = async (uid: string, focusId: string): Promise<void> => {
    const focusRef = doc(db, USER_CONTENT_COLLECTION, uid, FOCUS_SUBCOLLECTION, focusId);
    const today = getLocalDateString();

    try {
        const snap = await getDoc(focusRef);
        if (snap.exists()) {
            const lastViewed = snap.data()?.interactions?.lastInsightViewedAt;
            if (lastViewed) {
                const lastDate = getLocalDateString(lastViewed.toDate());
                if (lastDate === today) return;
            }
        }
    } catch {
        // On read failure, fall through and write anyway
    }

    await updateDoc(focusRef, {
        'interactions.insightViews': increment(1),
        'interactions.lastInsightViewedAt': serverTimestamp(),
        updatedAt: serverTimestamp(),
    });
};

/** Saves an insight reaction. `insightData.userReaction` keeps the latest
 *  (existing readers depend on it) while the top-level `reactions` array
 *  keeps every change, append-only. `at` is a client-clock ISO string —
 *  Firestore rejects serverTimestamp() inside arrayUnion. One write. */
export const saveInsightReaction = async (
    uid: string,
    focusId: string,
    reaction: string,
): Promise<void> => {
    const focusRef = doc(db, USER_CONTENT_COLLECTION, uid, FOCUS_SUBCOLLECTION, focusId);
    await updateDoc(focusRef, {
        'insightData.userReaction': reaction,
        reactions: arrayUnion({ reaction, at: new Date().toISOString() }),
        updatedAt: serverTimestamp(),
    });
};

export const markFocusViewed = async (uid: string, focusId: string): Promise<void> => {
    const focusRef = doc(db, USER_CONTENT_COLLECTION, uid, FOCUS_SUBCOLLECTION, focusId);

    // Only increment views once per calendar day. lastViewedAt is a server
    // timestamp — if it falls on today's local date the user already got
    // credit for viewing this item today.
    try {
        const snap = await getDoc(focusRef);
        if (snap.exists()) {
            const lastViewed = snap.data()?.interactions?.lastViewedAt;
            if (lastViewed) {
                const lastViewedDate = getLocalDateString(lastViewed.toDate());
                if (lastViewedDate === getLocalDateString()) return;
            }
        }
    } catch {
        // On read failure, fall through and write anyway
    }

    await updateDoc(focusRef, {
        'interactions.views': increment(1),
        'interactions.lastViewedAt': serverTimestamp(),
        updatedAt: serverTimestamp()
    });
};

/**
 * Get all responses for a focus item from the subcollection
 */
export const getFocusItemResponses = async (
    uid: string,
    focusId: string
): Promise<FocusItemResponse[]> => {
    try {
        const responsesRef = collection(
            db,
            USER_CONTENT_COLLECTION,
            uid,
            FOCUS_SUBCOLLECTION,
            focusId,
            FOCUS_RESPONSES_SUBCOLLECTION
        );
        const responsesQuery = query(responsesRef, orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(responsesQuery);

        return snapshot.docs.map((docSnap) => {
            const data = docSnap.data();
            return {
                id: docSnap.id,
                responses: data.responses || {},
                date: data.date || getLocalDateString(),
                completedAt: toIsoString(data.completedAt),
                createdAt: toIsoString(data.createdAt) || new Date().toISOString()
            };
        });
    } catch (error) {
        console.error('Error getting focus item responses:', error);
        return [];
    }
};

/** Mark today's session as complete on the activityDay doc */
export const saveSessionComplete = async (uid: string): Promise<void> => {
    const todayLocal = getLocalDateString();
    const ref = doc(db, USER_CONTENT_COLLECTION, uid, 'activityDays', todayLocal);
    await setDoc(ref, { sessionComplete: true, date: todayLocal }, { merge: true });
};

/** Check if today's session is already complete (one-shot read) */
export const isTodaySessionComplete = async (uid: string): Promise<boolean> => {
    const todayLocal = getLocalDateString();
    const ref = doc(db, USER_CONTENT_COLLECTION, uid, 'activityDays', todayLocal);
    const snap = await getDoc(ref);
    return snap.exists() && snap.data()?.sessionComplete === true;
};

/** Debug: clear session complete flag and reset the first completed/dismissed focus item to active */
export const debugResetSessionComplete = async (uid: string): Promise<void> => {
    // Clear sessionComplete on today's activityDay doc
    const todayLocal = getLocalDateString();
    const activityRef = doc(db, USER_CONTENT_COLLECTION, uid, 'activityDays', todayLocal);
    const activitySnap = await getDoc(activityRef);
    if (activitySnap.exists()) {
        await updateDoc(activityRef, { sessionComplete: deleteField() });
    }
    console.log('[Debug] Cleared sessionComplete from activityDays');

    // Reset the first completed or dismissed (non-layup) focus item back to active
    const focusRef = collection(db, USER_CONTENT_COLLECTION, uid, FOCUS_SUBCOLLECTION);
    const snapshot = await getDocs(query(focusRef));
    console.log('[Debug] Focus items:', snapshot.docs.map(d => {
        const data = d.data();
        return { id: d.id, status: data.status, cardType: data.cardType, isLayup: data.isLayupItem, contentType: data.contentType };
    }));
    let reset = false;
    for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const isLayup = data.isLayupItem || data.cardType === 'layup';
        if ((data.status === 'completed' || data.status === 'dismissed') && !isLayup) {
            await updateDoc(docSnap.ref, {
                status: 'active',
                completedAt: deleteField(),
                dismissedAt: deleteField(),
                updatedAt: serverTimestamp(),
            });
            console.log('[Debug] Reset focus item to active:', docSnap.id, data.contentType);
            reset = true;
            break;
        }
    }
    if (!reset) {
        console.warn('[Debug] No completed/dismissed non-layup focus items found to reset');
    }
};

/** Get all completed focus items for a user (one-off fetch, not real-time) */
export const getUserCompletedFocusItems = async (uid: string): Promise<UserFocusItem[]> => {
    const focusRef = collection(db, USER_CONTENT_COLLECTION, uid, FOCUS_SUBCOLLECTION);
    const q = query(focusRef, where('status', '==', 'completed'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(mapFocusDoc);
};

/** DEV ONLY: Delete all focus items and re-seed initial batch. */
export const debugResetToInitialBatch = async (uid: string): Promise<void> => {
    const focusRef = collection(db, USER_CONTENT_COLLECTION, uid, FOCUS_SUBCOLLECTION);
    const snapshot = await getDocs(focusRef);
    const batch = writeBatch(db);
    snapshot.docs.forEach(d => batch.delete(d.ref));
    await batch.commit();
    await refreshUserContent({
        contentTypes: ['focus'],
        seedDefaultsIfEmpty: true
    });
};

/** DEV ONLY: Fetch ALL focus items (no date/status filter). */
export const debugGetAllFocusItems = async (uid: string): Promise<UserFocusItem[]> => {
    const focusRef = collection(db, USER_CONTENT_COLLECTION, uid, FOCUS_SUBCOLLECTION);
    const snapshot = await getDocs(query(focusRef));
    return snapshot.docs.map(mapFocusDoc);
};
